import { useState } from 'react';

import {
  AdornedInput,
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Select,
  Text,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';
import {
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_numBathrooms,
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_numBedrooms,
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_squareFeet,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { bathOptions, bedOptions } from '../../../../constants/ListingDetailsConstants';

type Props = {
  isOpen: boolean;
  floorPlanId: string;
  onClose: () => void;
  onConfirm: () => void;
};

type SaveState = 'idle' | 'pending' | 'success' | 'error';

const PropertyDetailsStep = ({ isOpen, floorPlanId, onClose, onConfirm }: Props): JSX.Element => {
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [size, setSize] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [bedsSaveState, setBedsSaveState] = useState<SaveState>('idle');
  const [bathsSaveState, setBathsSaveState] = useState<SaveState>('idle');

  const { enqueueToast } = useToast();

  const { mutateAsync: updateBedrooms } = useRentalManagerUserPropertyFloorPlanEditWebApiHandler_numBedrooms({
    meta: { context: { api: 'rental-manager', details: 'update number of bedrooms' } },
  });
  const { mutateAsync: updateBathrooms } = useRentalManagerUserPropertyFloorPlanEditWebApiHandler_numBathrooms({
    meta: { context: { api: 'rental-manager', details: 'update number of bathrooms' } },
  });
  const { mutateAsync: updateSquareFeet } = useRentalManagerUserPropertyFloorPlanEditWebApiHandler_squareFeet({
    meta: { context: { api: 'rental-manager', details: 'update square feet' } },
  });

  const showBedsError = hasSubmitted && !beds;
  const showBathsError = hasSubmitted && !baths;
  const isSaving = bedsSaveState === 'pending' || bathsSaveState === 'pending';
  const hasSaveError = bedsSaveState === 'error' || bathsSaveState === 'error';

  const handleBedsChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setBeds(value);
    if (!value) return;
    setBedsSaveState('pending');
    try {
      await updateBedrooms({ getParams: { floorPlanId, numBedrooms: Number(value) } });
      setBedsSaveState('success');
    } catch (err) {
      logError({ error: err, context: 'PropertyDetailsStep.handleBedsChange' });
      setBedsSaveState('error');
    }
  };

  const handleBathsChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setBaths(value);
    if (!value) return;
    setBathsSaveState('pending');
    try {
      await updateBathrooms({ getParams: { floorPlanId, numBathrooms: Number(value) } });
      setBathsSaveState('success');
    } catch (err) {
      logError({ error: err, context: 'PropertyDetailsStep.handleBathsChange' });
      setBathsSaveState('error');
    }
  };

  const handleSizeBlur = () => {
    if (size) {
      updateSquareFeet({ getParams: { floorPlanId, squareFeet: Number(size) } }).catch((err) => {
        logError({ error: err, context: 'PropertyDetailsStep.handleSizeBlur' });
        enqueueToast(<Toast tone="critical">Square footage couldn’t be saved. Try again.</Toast>);
      });
    }
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    if (!beds || !baths || isSaving || hasSaveError) return;
    onConfirm();
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      header={<Heading level={2}>Tell us about your property</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
            <Text>
              Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your
              perfect renter.
            </Text>
          </Box>

          {bedsSaveState === 'error' && (
            <Alert tone="critical">Beds could not be saved. Select a value to retry.</Alert>
          )}
          {bathsSaveState === 'error' && (
            <Alert tone="critical">Baths could not be saved. Select a value to retry.</Alert>
          )}

          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
            <FormField
              required
              error={showBedsError}
              label={<Label>Beds</Label>}
              control={
                <Select data-testid="beds-select" value={beds} onChange={handleBedsChange}>
                  {bedOptions.map((opt: string) => {
                    if (opt === 'studio') {
                      return (
                        <option key={opt} value={0}>
                          {opt}
                        </option>
                      );
                    }
                    return (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    );
                  })}
                </Select>
              }
              description={showBedsError ? <FormHelp>Number of beds is required</FormHelp> : undefined}
            />

            <FormField
              required
              error={showBathsError}
              label={<Label>Baths</Label>}
              control={
                <Select data-testid="baths-select" value={baths} onChange={handleBathsChange}>
                  {bathOptions.map((opt: string) => {
                    if (opt === '10+') {
                      return (
                        <option key={opt} value={10}>
                          {opt}
                        </option>
                      );
                    }
                    return (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    );
                  })}
                </Select>
              }
              description={showBathsError ? <FormHelp>Number of bathrooms is required</FormHelp> : undefined}
            />

            <FormField
              optional
              label={<Label>Size</Label>}
              control={
                <AdornedInput
                  input={
                    <AdornedInput.Input
                      data-testid="size-input"
                      type="text"
                      inputMode="numeric"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      onBlur={handleSizeBlur}
                    />
                  }
                  endAdornment={<AdornedInput.Adornment>sqft</AdornedInput.Adornment>}
                />
              }
            />
          </Box>
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Property details actions">
          <Button fluid={false} tone="brand" emphasis="bare" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-testid="property-details-continue-button"
            fluid={false}
            tone="brand"
            emphasis="filled"
            loading={isSaving}
            onClick={handleSubmit}
          >
            Continue
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default PropertyDetailsStep;
